import type { AlertColor } from '@mui/material'

import { useEffect, useState } from 'react'

import { useAuth } from '../../auth/hooks/useAuth'
import type { User } from '../../auth/types/auth.types'
import { dropdownApi, dropdownRequestApi } from '../../dropdown/services/dropdownApi'
import type {
  DropdownOptionsByField,
  OptionRequestDialogState,
  OptionRequestFormState,
} from '../../dropdown/types/dropdown.types'
import { articleApi } from '../services/articleApi'
import type { ApiError } from '../../../shared/types/common'

type ShowSnackbar = (message: string, severity?: AlertColor) => void

export function useArticleMetadata(showSnackbar: ShowSnackbar) {
  const { user } = useAuth()
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptionsByField>({})
  const [workflowUsers, setWorkflowUsers] = useState<User[]>([])
  const [requestDialog, setRequestDialog] = useState<OptionRequestDialogState>({ open: false, fieldName: '', label: '' })
  const [requestForm, setRequestForm] = useState<OptionRequestFormState>({ requested_label: '', justification: '' })

  useEffect(() => {
    void loadDropdownOptions()
    void loadWorkflowUsers()
  }, [])

  async function loadDropdownOptions() {
    try {
      const response = await dropdownApi.getAll()
      setDropdownOptions(response.data)
    } catch {
      // Keep silent to avoid blocking the form shell during initial render.
    }
  }

  async function loadWorkflowUsers() {
    try {
      const response = await articleApi.listUsers()
      setWorkflowUsers(response.data)
    } catch {
      // Keep silent to avoid blocking form rendering due to optional workflow helpers.
    }
  }

  function openRequestDialog(fieldName: string, label: string) {
    setRequestDialog({ open: true, fieldName, label })
    setRequestForm({ requested_label: '', justification: '' })
  }

  async function handleRequestOption() {
    if (!requestForm.requested_label.trim()) {
      showSnackbar('Requested value is required', 'error')
      return
    }

    try {
      await dropdownRequestApi.create({
        field_name: requestDialog.fieldName,
        requested_label: requestForm.requested_label,
        justification: requestForm.justification,
      })
      showSnackbar('Option request submitted for admin review')
      setRequestDialog({ open: false, fieldName: '', label: '' })
      setRequestForm({ requested_label: '', justification: '' })
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to submit request', 'error')
    }
  }

  return {
    currentUserRole: user?.role,
    dropdownOptions,
    handleRequestOption,
    openRequestDialog,
    requestDialog,
    requestForm,
    setRequestDialog,
    setRequestForm,
    workflowUsers,
  }
}
